import { TextInput } from '@loomhq/lens';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ROLLOUT_VIDEO_PLAYER_END_SCREEN } from 'constants/featureFlag';
import { ENTER } from 'constants/keyCodes';
import { updateUsername } from 'creators/videoPlayer';
import { getAnonUserName } from 'utilities/auth';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { useWidthQuery } from 'hooks/useMedia';
import { selectUserIsLogged } from 'selectors/user';
import { selectCommentsEnabled, selectEmojiEnabled } from 'selectors/video';
import { selectHasEnded } from 'selectors/videoPlayer';

import CommentButton from 'components/ui-components/comment-button';
import EmojiReactionSet from 'components/ui-components/emoji-reaction-bar';
import { RecordReplyWrapper } from 'components/video-player/end-screen/record-reply-fallback-button';

import styles from './styles.module.less';

const EngagementBar = () => {
  const [hasRecordReply, setHasRecordReply] = useState(false);
  const dispatch = useDispatch();
  const ref = useRef();

  const emojiEnabled = useSelector(selectEmojiEnabled);
  const showCommentButton = useSelector(selectCommentsEnabled);
  const isLoggedIn = useSelector(selectUserIsLogged);
  const videoHasEnded = useSelector(selectHasEnded);

  const forceMultiLine = useWidthQuery(ref, {
    minWidth: 0,
    maxWidth: 465,
    defaultValue: false,
  });

  const useSmallIcons = useWidthQuery(ref, {
    minWidth: 466,
    maxWidth: 512,
    defaultValue: false,
  });

  const multiLineEngagementBar = hasRecordReply && forceMultiLine;
  const forceSmallIcon = hasRecordReply && useSmallIcons;

  const newEndScreenFeature = useFlagIsActivated({
    flag: ROLLOUT_VIDEO_PLAYER_END_SCREEN,
    activationValues: ['variant'],
  });

  const componentDisabled = videoHasEnded && !newEndScreenFeature;

  const [askForName, setAskForName] = useState(false);
  const [anonName, setAnonName] = useState('');

  function checkAnonName() {
    if (!isLoggedIn && !getAnonUserName()) {
      setAskForName(true);
    }
  }

  function sendNameUpdate() {
    dispatch(updateUsername(anonName));
    setAskForName(false);
  }

  const engagementBarClass = multiLineEngagementBar
    ? styles.multiLineEngagementBar
    : styles.engagementBar;

  return (
    <div ref={ref} className={styles.engagementBarContainer}>
      <div className={engagementBarClass}>
        {!askForName && (
          <>
            {emojiEnabled && (
              <div className={styles.engagementSection}>
                <EmojiReactionSet
                  disabled={componentDisabled}
                  onClick={checkAnonName}
                  forceSmallIcon={forceSmallIcon}
                />
              </div>
            )}
            {emojiEnabled && showCommentButton && !multiLineEngagementBar && (
              <div className={styles.verticalRule} />
            )}
            {showCommentButton && (
              <div className={styles.engagementSection}>
                <CommentButton
                  disabled={componentDisabled}
                  buttonText="Comment"
                  onEmbedPlayer={false}
                  forceSmallIcon={forceSmallIcon}
                />

                {/*
                Record  reply cannot be enabled without comments being enabled, so it's
                safe to include this component inside the wrapping conditional
             */}
                <RecordReplyWrapper
                  iconButton={true}
                  setHasRecordReply={setHasRecordReply}
                  forceSmallIcon={forceSmallIcon}
                />
              </div>
            )}
          </>
        )}
        {askForName && (
          <div className={styles.engagementSection}>
            <TextInput
              data-testid="anon-reaction-name-input"
              autoFocus={true}
              placeholder="Your name..."
              className={styles.anonInput}
              onChange={ev => setAnonName(ev.target.value)}
              onKeyUp={ev => {
                if (anonName.trim().length && ev.keyCode === ENTER) {
                  sendNameUpdate();
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EngagementBar;
